/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  AdminDollarInput2,
  AdminInput,
  Button,
  Captcha,
  CaptchaSlot,
  Div,
  ErrorToast,
  H3,
  Img,
  Modal,
  SuccessToast,
  deductBankAmount,
  increaseBankAmount,
  moneyCoins,
  useCaptcha,
  useUsers,
  Wallets,
  UserRole,
  reportError,
} from '@vgrubs/components';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

export const UserCreditCardEditBalance = ({
  userId,
  getBank,
  editBalanceModal,
  setEditBalanceModal,
  wallet,
  userRole,
  setLoading,
}: {
  userId: number | undefined;
  getBank: () => void;
  editBalanceModal: Wallets | boolean | null;
  setEditBalanceModal: (v: Wallets | boolean | null) => void;
  wallet?: Wallets;
  userRole: UserRole;
  setLoading: (v: boolean) => void;
}) => {
  const [amount, setAmount] = useState(0);
  const [adminPassword, setAdminPassword] = useState('');
  const [reason, setReason] = useState('');
  const captchaRef = useRef<HTMLDivElement>(null);
  const { getCaptchaToken } = useCaptcha(
    process.env.NX_TURNSTILE_SITE_KEY_BALANCE,
    'admin-balance',
    captchaRef,
    // Hide the loader while the user is solving an interactive challenge.
    (isInteractive) => isInteractive && setLoading(false)
  );

  const { bank } = useUsers((state) => state);

  const availableBalance =
    userRole === UserRole.partner
      ? wallet?.balance?.availableBalance || 0
      : bank?.balance?.availableBalance || 0;

  // One token backs whichever of the two endpoints fires — the branches below
  // are mutually exclusive, so the single-use token is never spent twice.
  async function handleEditBalance(captcha: Captcha) {
    try {
      setLoading(true);

      if (Number(amount) < Number(availableBalance)) {
        const amountToDeduct = Number(availableBalance) - Number(amount);

        const body = {
          amount: amountToDeduct,
          adminPassword,
          captcha,
          reason,
          locationId: wallet?.simpleLocation?.id || undefined,
        };

        const { data } = await deductBankAmount(userId!, body);
        await getBank();
        SuccessToast(data.message);
      }

      if (Number(amount) > Number(availableBalance)) {
        const amountToIncrease = Number(amount) - Number(availableBalance);

        const body = {
          amount: amountToIncrease,
          adminPassword,
          captcha,
          reason,
          locationId: wallet?.simpleLocation?.id || undefined,
        };

        const { data } = await increaseBankAmount(
          userId!,
          body,
          wallet?.simpleLocation?.id || undefined
        );
        await getBank();
        SuccessToast(data.message);
      }

      setEditBalanceModal(null);
      setAmount(0);
      setAdminPassword('');
      setReason('');
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async () => {
    if (!amount) {
      return ErrorToast('Please enter a balance');
    }

    if (!reason.trim()) {
      return ErrorToast('Please enter a reason');
    }

    if (!adminPassword.trim()) {
      return ErrorToast('Please enter the admin password');
    }

    if (Number(amount) === availableBalance)
      return ErrorToast('Please enter a different balance');

    setLoading(true);
    const token = await getCaptchaToken();
    if (!token) {
      setLoading(false);
      return;
    }

    handleEditBalance({ token });
  };

  useEffect(() => {
    if (bank || wallet) {
      setAmount(
        userRole === UserRole.partner
          ? wallet?.balance?.availableBalance || 0
          : bank?.balance?.availableBalance || 0
      );
    }
  }, [bank, wallet, editBalanceModal]);

  const handleAmountChange = useCallback((val) => {
    setAmount(val);
  }, []);

  return (
    <Modal
      isOpen={!!editBalanceModal}
      closeModal={() => setEditBalanceModal(null)}
      logo={false}
      // header="Add Bank"
    >
      <Img width={230} src={moneyCoins} alt="edit balance" />
      <H3 $m="24px 0">Edit Balance</H3>
      <Div $flex $column $gap="20px" $gapSM="16px">
        <AdminDollarInput2
          label="Available Balance"
          onValueChange={handleAmountChange}
          value={amount}
          $textCenter
        />

        <AdminInput
          label="Reason"
          placeholder="Why is the balance being corrected?"
          value={reason}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setReason(e.target.value)
          }
          type="textarea"
          $textareaHeight={120}
        />

        <AdminInput
          label="Admin Password"
          placeholder="Enter here"
          value={adminPassword}
          onChange={(e) => setAdminPassword(e.target.value)}
          $password
          $textCenter
          type="text"
          autoComplete="off"
        />
      </Div>

      <CaptchaSlot ref={captchaRef} />

      <Button $selected $w="100%" $mt="12" $mtSM="16" onClick={handleSubmit}>
        Save
      </Button>
      <Button
        $selected
        $gray
        $w="100%"
        $mt="16"
        onClick={() => setEditBalanceModal(null)}
      >
        Cancel
      </Button>
    </Modal>
  );
};
